import Flex from '@react-css/flex';
import Spinner from 'components/Common/Spinner';
import {
  SettingsDescription,
  SettingsPageHeader,
  SettingsSectionTitle,
} from 'containers/SettingsPage/SettingsLayout';
import useAlertQueue from 'hooks/useAlertQueue';
import { useBalanceVendorOnboarding } from 'hooks/useBalanceRedirect';
import { Badge } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useAppDispatch, useAppSelector } from 'store';
import { selectCompany } from 'store/selectors/me/company';
import { selectUser } from 'store/selectors/me/user';
import { connectStripe, createBillingPortal, createUSBillingPortal } from 'store/slices/stripe';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';
import SupportLink from 'storybookConfig/stories/molecules/Link/SupportLink';
import { ACCOUNT_TYPE_BUYER, ACCOUNT_TYPE_SELLER } from 'utils/constants';
import BalanceBuyerSettings from './BalanceBuyerSettings';
import BillingSettingsForm from './BillingSettingsForm';

const ManagePaymentMethod = ({
  commerceType,
  hasUS,
  onPortalButtonClick,
  isPortalButtonDisabled,
  onUsPortalButtonClick,
  isUsPortalButtonDisabled,
}) => {
  return (
    <>
      <SettingsSectionTitle>Manage Payment Method</SettingsSectionTitle>
      <SettingsDescription>
        You can manage your payment method (credit card) through the Stripe billing portal.{' '}
        {commerceType === ACCOUNT_TYPE_SELLER
          ? 'Suppliers on the standard payment plan do not need to add a payment method.'
          : "This card will receive the charges for both your Carro platform fee as well as for your supplier's invoices."}
      </SettingsDescription>
      {hasUS === true && (
        <SettingsDescription>
          To see subscriptions and invoices issued from our US Stripe account, go to the US Billing
          Portal.
        </SettingsDescription>
      )}
      <div className="d-flex">
        <SecondaryButton
          size="small"
          onClick={onPortalButtonClick}
          disabled={isPortalButtonDisabled}
        >
          {isPortalButtonDisabled ? (
            <Spinner color="primary" className="mx-4" small />
          ) : (
            'Access Billing Portal'
          )}
        </SecondaryButton>

        {hasUS === true && (
          <SecondaryButton
            size="small"
            onClick={onUsPortalButtonClick}
            disabled={isUsPortalButtonDisabled}
            className="ml-2"
          >
            {isUsPortalButtonDisabled ? (
              <Spinner color="primary" className="mx-4" small />
            ) : (
              'Access US Billing Portal'
            )}
          </SecondaryButton>
        )}
      </div>
    </>
  );
};

const ConnectStripe = ({ stripeConnected, gateway, stripeAccountId, disabled, onClick }) => {
  return (
    <>
      <hr style={{ margin: '24px 0' }} />
      <SettingsSectionTitle>
        Connect Stripe Account
        {stripeConnected && (
          <Badge variant="success" pill className="ml-3">
            Connected
          </Badge>
        )}
      </SettingsSectionTitle>
      <SettingsDescription>
        {stripeConnected &&
          'You have successfully connected a Stripe account. You can manage the details of that account through Stripe.'}

        {!stripeConnected &&
          (gateway === 'other'
            ? 'You have chosen to manage your own billing for orders. Connect your Stripe account if you would like to automate it.'
            : 'As a seller, you can automatically bill your customers by connecting a Stripe account. Once connected, this account will receive payouts for orders processed through Carro.')}

        {gateway !== 'stripe' && (
          <a href="https://stripe.com/en-ca/pricing" target="_blank" rel="noopener noreferrer">
            {' '}
            Read about Stripe&#39;s terms.
          </a>
        )}
      </SettingsDescription>
      {gateway !== 'stripe' || !stripeAccountId ? (
        <div className="d-flex">
          <SecondaryButton size="small" disabled={disabled} onClick={onClick}>
            {disabled ? <Spinner color="primary" className="mx-3" small /> : 'Connect Stripe'}
          </SecondaryButton>
        </div>
      ) : null}
      <SettingsDescription style={{ marginTop: 12 }}>
        <strong>Important: </strong>You will need to configure tax rates in Stripe.{' '}
        <SupportLink href="https://moderndropship.frontkb.com/en/articles/9632897">
          Learn more about enabling taxes
        </SupportLink>
        .
      </SettingsDescription>
    </>
  );
};

const BillingSettings = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const user = useAppSelector(selectUser);
  const alertQueue = useAlertQueue();
  const {
    openOnboarding: openBalanceVendorOnboarding,
    isRedirecting: isRedirectingToBalanceVendor,
  } = useBalanceVendorOnboarding();
  const { isConnecting, isCreatingBillingPortal, isCreatingUSBillingPortal } = useSelector(
    (state) => state.stripe
  );
  const stripeConnected = company.gateway === 'stripe' && company.stripeAccountId;
  const isBuyer = company.commerceType === ACCOUNT_TYPE_BUYER;
  const balanceEnabled = !!company?.flags?.balanceEnabled;
  const balanceVendorOnboarded = !!company?.balance?.vendorId;
  const vendorOnboardingButtonLabel = balanceVendorOnboarded
    ? 'Update Balance Details'
    : 'Onboard to Balance';

  const accessBillingPortal = () => {
    dispatch(createBillingPortal()).then((action) => {
      if (action.error) {
        alertQueue.addErrorAlert('Something went wrong', action.error.message);
      }
    });
  };

  const accessUSBillingPortal = () => {
    dispatch(createUSBillingPortal()).then((action) => {
      if (action.error) {
        alertQueue.addErrorAlert('Something went wrong', action.error.message);
      }
    });
  };

  const connectStripeAccount = () => {
    dispatch(connectStripe()).then((action) => {
      if (action.error) {
        alertQueue.addErrorAlert('Something went wrong', action.error.message);
      }
    });
  };

  const openBalanceVendorOnboardingModal = () => {
    openBalanceVendorOnboarding().catch((err) => {
      alertQueue.addErrorAlert(
        'Something went wrong',
        err?.message ?? 'Failed to open Balance vendor onboarding'
      );
    });
  };

  return (
    <div style={{ padding: 24 }}>
      <SettingsPageHeader title="Billing" />

      <Flex column gap="24px">
        <ManagePaymentMethod
          commerceType={company.commerceType}
          hasUS={user?.subscriptions?.hasUS}
          onPortalButtonClick={accessBillingPortal}
          isPortalButtonDisabled={isCreatingBillingPortal}
          onUsPortalButtonClick={accessUSBillingPortal}
          isUsPortalButtonDisabled={isCreatingUSBillingPortal}
        />

        {isBuyer && balanceEnabled && <BalanceBuyerSettings />}

        {company.commerceType === ACCOUNT_TYPE_SELLER && balanceEnabled && (
          <>
            <hr className="my-4" />
            <SettingsSectionTitle>
              Balance Vendor Onboarding
              {balanceVendorOnboarded && (
                <Badge variant="success" pill className="ml-3">
                  Onboarded
                </Badge>
              )}
            </SettingsSectionTitle>
            <SettingsDescription>
              {balanceVendorOnboarded
                ? 'Your Balance vendor account is set up. You can update your details by clicking below.'
                : 'Complete Balance vendor onboarding to receive payouts and manage your seller account with Balance.'}
            </SettingsDescription>
            <div className="d-flex mb-2">
              <PrimaryButton
                size="small"
                onClick={openBalanceVendorOnboardingModal}
                disabled={isRedirectingToBalanceVendor}
              >
                {isRedirectingToBalanceVendor ? (
                  <Spinner color="primary" className="mx-4" small />
                ) : (
                  vendorOnboardingButtonLabel
                )}
              </PrimaryButton>
            </div>
          </>
        )}

        {company.commerceType === ACCOUNT_TYPE_SELLER && (
          <ConnectStripe
            stripeConnected={stripeConnected}
            gateway={company.gateway}
            stripeAccountId={company.stripeAccountId}
            disabled={isConnecting}
            onClick={connectStripeAccount}
          />
        )}

        {isBuyer && <BillingSettingsForm company={company} />}
      </Flex>
    </div>
  );
};

export default BillingSettings;
